import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * DocumentTitle — keeps the browser tab title in sync with the current route.
 *
 * Additive, render-nothing helper mounted once inside the Router. It maps the
 * active pathname to an Arabic page label and writes `"<label> — تاجر برو"`.
 * No new dependencies; uses the router's own `useLocation`.
 */

const BRAND = 'تاجر برو';

// Ordered [prefix, label] rules — first match wins, so more specific paths
// must precede their less specific parents (e.g. /banks/reconciliation before
// /banks). A pathname matches when it equals the prefix or sits under it.
const TITLE_RULES = [
  ['/dashboard', 'لوحة القيادة'],
  ['/customers', 'العملاء'],
  ['/suppliers', 'الموردون'],
  ['/inventory', 'المخزون'],
  ['/products', 'المنتجات'],
  ['/employees', 'الموظفون'],
  ['/payroll', 'الرواتب'],
  ['/hr-intelligence', 'ذكاء الموارد البشرية'],
  ['/hr', 'الموارد البشرية'],
  ['/approvals', 'الاعتمادات'],
  ['/portal', 'بوابة الموظف'],
  ['/automation', 'الأتمتة'],
  ['/payments', 'المدفوعات'],
  ['/recurring-invoices', 'الفواتير المتكررة'],
  ['/invoices', 'الفواتير'],
  ['/sales', 'المبيعات'],
  ['/purchase-orders', 'أوامر الشراء'],
  ['/purchases', 'المشتريات'],
  ['/expenses', 'المصروفات'],
  ['/reports', 'التقارير'],
  ['/quotations', 'عروض الأسعار'],
  ['/crm', 'إدارة علاقات العملاء'],
  ['/manufacturing/bom', 'قوائم المواد'],
  ['/manufacturing', 'التصنيع'],
  ['/warehouses', 'المستودعات'],
  ['/branches', 'الفروع'],
  ['/promotions', 'العروض الترويجية'],
  ['/loyalty', 'برنامج الولاء'],
  ['/stock-moves', 'حركات المخزون'],
  ['/trade-dashboard', 'لوحة التجارة الدولية'],
  ['/customs', 'التخليص الجمركي'],
  ['/shipments', 'الشحنات'],
  ['/free-zones', 'المناطق الحرة'],
  ['/supply-chain', 'سلسلة الإمداد'],
  ['/compliance', 'الامتثال'],
  ['/re-export', 'إعادة التصدير'],
  ['/risk-assessment', 'تقييم المخاطر'],
  ['/cost-optimization', 'تحسين التكاليف'],
  ['/journal-entries', 'القيود المحاسبية'],
  ['/financial-reports', 'التقارير المالية'],
  ['/banks/reconciliation', 'التسوية البنكية'],
  ['/banks', 'البنوك'],
  ['/trash', 'المحذوفات'],
  ['/accounting/close-dashboard', 'إقفال الفترة المحاسبية'],
  ['/accounting', 'المحاسبة'],
  ['/ai-assistant', 'المساعد الذكي'],
  ['/projects', 'إدارة المشاريع'],
  ['/ecommerce', 'المتجر الإلكتروني'],
  ['/pos/sessions', 'جلسات نقاط البيع'],
  ['/pos/reports', 'تقارير نقاط البيع'],
  ['/pos/operations-control-center', 'مركز قيادة نقاط البيع'],
  ['/pos', 'نقاط البيع'],
  ['/users', 'المستخدمون'],
  ['/settings/vat', 'إعدادات الضريبة'],
  ['/settings', 'الإعدادات'],
  ['/zatca', 'الفوترة الإلكترونية'],
  ['/profile', 'الملف الشخصي'],
  ['/notifications', 'الإشعارات'],
  ['/vat-return', 'الإقرار الضريبي'],
  ['/digital-twin', 'التوأم الرقمي'],
  ['/fraud-watchdog', 'مراقب الاحتيال'],
  ['/admin/observability', 'المراقبة التشغيلية'],
  ['/neural-cloud', 'السحابة العصبية'],
  ['/operations-readiness-index', 'مؤشر جاهزية العمليات'],
  ['/zero-ui', 'الواجهة الصفرية'],
  ['/login', 'تسجيل الدخول'],
  ['/register', 'إنشاء حساب'],
  ['/storefront', 'المتجر'],
  ['/customer-portal', 'بوابة العملاء'],
  ['/vendor-portal', 'بوابة الموردين'],
];

const titleForPath = (pathname) => {
  const rule = TITLE_RULES.find(
    ([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
  return rule ? rule[1] : '';
};

export default function DocumentTitle() {
  const { pathname } = useLocation();

  useEffect(() => {
    const label = titleForPath(pathname);
    document.title = label ? `${label} — ${BRAND}` : BRAND;
  }, [pathname]);

  return null;
}
